import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { TAGLINE } from '@mockd/shared/brand';
import { Cursor, LoadingLabel } from '../common/Cursor';
import { ProductPreview, PREVIEW_BODY, PREVIEW_LOG, PREVIEW_URL } from './ProductPreview';
import { Prompt, Terminal } from './Terminal';

interface HeroProps {
  onQuickStart: () => void;
  isLoading?: boolean;
}

export function Hero({ onQuickStart, isLoading }: HeroProps) {
  const newest = PREVIEW_LOG[0];

  return (
    <section>
      <div className="max-w-6xl mx-auto px-4 pt-16 pb-20 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          {/* The page's one headline `_` (docs/brand.md → The `_` cursor). */}
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight">
            {TAGLINE}
            <Cursor className="ml-2" />
          </h1>
          {/* META_DESCRIPTION, with the URL set in mono. */}
          <p className="mt-6 text-lg text-base-content/80 max-w-xl leading-relaxed">
            Create mock API endpoints at{' '}
            <code className="text-primary font-mono text-base bg-primary/10 px-1.5 py-0.5 rounded-field">
              your-project.mockd.sh
            </code>{' '}
            and watch every request arrive in real time. No server code, no signup.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={onQuickStart}
              disabled={isLoading}
              className="btn btn-primary btn-lg"
            >
              {isLoading ? (
                <LoadingLabel>Creating your mock</LoadingLabel>
              ) : (
                <>
                  Start mocking — free
                  <ArrowRightIcon className="size-5 ml-1" />
                </>
              )}
            </button>
            <Link to="/register" className="btn btn-outline btn-lg">
              Sign up free
            </Link>
          </div>
          <p className="mt-4 text-sm text-base-content/70">
            One click to a live URL. No account needed.
          </p>
        </div>

        {/* The request below is the one that lands at the top of the log. */}
        <div className="mt-14 space-y-3">
          <Terminal>
            <span className="whitespace-pre">
              <Prompt />
              curl -X {newest.method} {PREVIEW_URL}{newest.path} -d '{PREVIEW_BODY}'
            </span>
          </Terminal>
          <ProductPreview />
        </div>
      </div>
    </section>
  );
}
